import { computed, onMounted } from 'vue'

import type { ComputedRef } from 'vue'
import type { ExtraRatesSlot } from './interfaces'
import type { ShortDaysOfWeek } from '@brighterly/lib-core-types/options/enums'
import type { RateField } from './types'
import type { NativeSelectOption } from '@shared/ui/fields'

import { useExtraRatesStore } from './store'
import { EXTRA_RATE_VALUES, EXTRA_RATE_MULTIPLIER } from '../../config/extra-rates'

interface SectionExtraRatesTablePayload {
  id: ComputedRef<ShortDaysOfWeek>
}

interface SectionExtraRatesTableReturn {
  refSlots: ComputedRef<ExtraRatesSlot[]>
  rateOptions: NativeSelectOption<number>[]
  disabledRateOption: NativeSelectOption<number>[]
  setToggleSlot: (enableExtraRates: boolean, slotId: number) => void
  setChangeRate: (option: NativeSelectOption<number>, slotId: number, field: RateField) => void
  isSlotEnabled: (slotId: number) => boolean
}

export const useSectionExtraRatesTable = (payload: SectionExtraRatesTablePayload): SectionExtraRatesTableReturn => {
  const extraRatesStore = useExtraRatesStore()

  const weekId = payload.id.value

  const disabledRateOption: NativeSelectOption<number>[] = [
    { id: 0, value: 0, text: '$ 0.00', selected: true, disabled: true },
  ]

  const rateOptions: NativeSelectOption<number>[] = EXTRA_RATE_VALUES.map(value => ({
    id: value * EXTRA_RATE_MULTIPLIER,
    value: value * EXTRA_RATE_MULTIPLIER,
    text: `$ ${value}`,
    selected: false,
  }))

  const defaultRateValue = rateOptions[0]?.value ?? 0

  const refCurrentDay = computed(() => extraRatesStore.getDaysOfWeek.find(item => item.day_of_week === weekId))

  const refSlots = computed<ExtraRatesSlot[]>(() => refCurrentDay.value?.slots ?? [])

  const normalizeSlotRates = (slotData: ExtraRatesSlot): void => {
    if (!slotData.rates.complete) {
      slotData.rates.complete = defaultRateValue
    }
    if (!slotData.rates.no_show) {
      slotData.rates.no_show = defaultRateValue
    }
  }

  onMounted(() => {
    refCurrentDay.value?.slots.forEach((slot) => {
      if (slot.enable_extra_rates) {
        normalizeSlotRates(slot)
      }
    })
  })

  const setToggleSlot = (enableExtraRates: boolean, slotId: number): void => {
    const slotData = refCurrentDay.value?.slots[slotId]
    if (!slotData) return

    slotData.enable_extra_rates = enableExtraRates

    if (enableExtraRates) {
      normalizeSlotRates(slotData)
    }
  }

  const setChangeRate = (option: NativeSelectOption<number>, slotId: number, field: RateField): void => {
    const slotData = refCurrentDay.value?.slots[slotId]
    if (slotData) {
      slotData.rates[field] = option.value
    }
  }

  const isSlotEnabled = (slotId: number): boolean => {
    return !!refCurrentDay.value?.slots[slotId]?.enable_extra_rates
  }

  return {
    refSlots,
    rateOptions,
    disabledRateOption,
    setToggleSlot,
    setChangeRate,
    isSlotEnabled,
  }
}
